import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { instancesPrice } from 'src/app/constants/pricing';
import { UserI } from 'src/app/interfaces/user.interface';
import { ApiSdkService } from 'src/app/services/api-sdk.service';
import { ResultHandlerService } from 'src/app/services/result-handler.service';
import { phoneExtensions } from 'src/assets/international-phone-extensions/int-phone-extensions';
import { TranslateService } from '@ngx-translate/core';
import { LegacyHintService } from '../../services/legacy-hint.service';
import { environment } from 'src/environments/environment';

@Component({
  templateUrl: './user-data.page.html',
  styleUrls: ['./user-data.page.scss']
})
export class UserDataPage {
  userForm!: FormGroup;

  userData: UserI = null as any;

  emailToVerify: string = '';
  secretToVerify: string = '';

  pricesIntances = instancesPrice;

  userCard: any = null;
  
  editMode: boolean = false;

  // VCaaS email notification preferences (moved here from the API page)
  emailOnFirstPromptCompleted: boolean = true;
  emailOnLimitReached: boolean = true;
  savingNotifications: boolean = false;

  tooltipVisible: boolean = false;
  tooltipPosition = { top: '0px', left: '0px', width: '0px' };
  searchTerm: string = '';

  phoneExtensions = phoneExtensions; // Your initial large array of phone extensions

  @ViewChild('tooltip', { static: false }) tooltipRef!: ElementRef;
  @ViewChild('triggerInput', { static: false }) triggerInputRef!: ElementRef;

  constructor(
    public formBuilder: FormBuilder,
    private router: Router,
    private apiSdkService: ApiSdkService,
    private route: ActivatedRoute,
    private resultHandlerService: ResultHandlerService,
    private alertController: AlertController,
    private translate: TranslateService,
    private legacyHint: LegacyHintService,
    ) { }

  async ngOnInit() {



    // City, postal code, address and business information are intentionally
    // not editable here, so they are excluded from the editable form.
    this.userForm = this.formBuilder.group({
      email: [{value:'', disabled: true}],
      name: [{value: '', disabled: true}, [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      phone: [{value: '', disabled: true}, [Validators.required, Validators.minLength(9), Validators.maxLength(15), Validators.pattern('[- +()0-9]+')]],
      country: [{value: '', disabled: true}, [Validators.required,Validators.minLength(3), Validators.maxLength(100), this.validateCountryName()]]
    });
    await this.getUserData();
    
    await this.getUserStripeCard();
  }

  get errorControl() {
    return this.userForm.controls;
  }

  
  async getUserStripeCard() {
    const cardsResult = await this.apiSdkService.getUserStripeCard();
    this.userCard = cardsResult.success.data;
  }

  async getUserData() {
    const result = await this.apiSdkService.getFullUserData();
    this.userData = result.success.data;
    this.userForm.patchValue(this.userData);

    // Load VCaaS notification preferences (default to true)
    const notifications = (this.userData as any)?.vcaasNotifications;
    this.emailOnFirstPromptCompleted = notifications?.emailOnFirstPromptCompleted ?? true;
    this.emailOnLimitReached = notifications?.emailOnLimitReached ?? true;
  }

  async saveNotificationPreferences() {
    this.savingNotifications = true;
    try {
      await this.apiSdkService.updateVcaasNotifications({
        emailOnFirstPromptCompleted: this.emailOnFirstPromptCompleted,
        emailOnLimitReached: this.emailOnLimitReached,
      });
    } catch (error) {
      console.error('Error saving notification preferences:', error);
    } finally {
      this.savingNotifications = false;
    }
  }

  async onSubmit() {


    // Trigger form validation for all fields by marking them as touched
    Object.values(this.userForm.controls).forEach(control => {
      control.markAsTouched();
    });

    // Check if the form is valid before proceeding with form submission
    if (this.userForm.valid) {
        // Build user data
        const userData: any = { ...this.userForm.value };

        // Check for stored origin data (from referral link)
        const storedOrigin = localStorage.getItem('origin');
        if (storedOrigin) {
          try {
            userData.origin = JSON.parse(storedOrigin);
            // Clear from localStorage after sending
            localStorage.removeItem('origin');
          } catch (e) {
            console.error('Error parsing stored origin data:', e);
          }
        }

        await this.apiSdkService.editUserData(userData);
        this.resultHandlerService.showToast({message: 'Datos guardados', color: 'success'});
        // if is the first time user is created (name is null), redirect to account page
        window.location.reload();

        /*if(!this.userData?.name) {
          this.router.navigate(['/account']);
          setTimeout(() => {
            window.location.reload();
          }, 100); 
        }*/
    }
  }

  async logout() {
    const translations = await this.translate.get([
      'LOGOUT_CONFIRM.TITLE',
      'LOGOUT_CONFIRM.MESSAGE',
      'LOGOUT_CONFIRM.CANCEL',
      'LOGOUT_CONFIRM.CONFIRM',
    ]).toPromise();

    const alert = await this.alertController.create({
      header: translations['LOGOUT_CONFIRM.TITLE'],
      message: translations['LOGOUT_CONFIRM.MESSAGE'],
      buttons: [
        {
          text: translations['LOGOUT_CONFIRM.CANCEL'],
          role: 'cancel',
          cssClass: 'secondary',
          handler: (blah) => {
            console.log('Confirm Cancel: blah');
          }
        }, {
          text: translations['LOGOUT_CONFIRM.CONFIRM'],
          handler: () => {
            localStorage.removeItem('token');
            /* ⚠️ See `LegacyHintService.clear` — beside every token removal. */
            this.legacyHint.markSignedOut();

            /**
             * ⭐ SIGNING OUT LANDS ON THE NEW PLATFORM, NOT ON OUR OWN `/login`.
             * This panel is legacy; the product a departing user should see is
             * `platform.totalum.app`.
             *
             * ⚠️ IT MUST STAY AFTER `markSignedOut()`. That writes `tot_legacy=0`,
             * which is what stops the platform bouncing the arrival straight back
             * here via its `/legacy-account` handoff. Navigating first would race
             * the cookie write against the unload.
             *
             * ⚠️ `location.href`, NOT `router.navigate` — this is a different
             * origin. The full navigation also replaces the old in-memory state,
             * which is what the `reload()` this replaced was there to do.
             */
            window.location.href = environment.platformUrl;
          }
        }
      ]
    });
  
    await alert.present();
  }

  async getData(){
    const userResult = await this.apiSdkService.getFullUserData();
    this.userData = userResult.success.data;
    const cardsResult = await this.apiSdkService.getUserStripeCard();
    this.userCard = cardsResult.success.data;
  }

  async uploadCardFinish() {
    //this.changeCard = false;
    await this.getData();
  }

  calculeNextDayToPay(instanceCreatedAt: Date | null | undefined) {
    if (!instanceCreatedAt) return '';

    const currentDate = new Date();
    const nextPaymentDate = new Date(instanceCreatedAt);
    // Set nextPaymentDate to the same year as currentDate
    nextPaymentDate.setFullYear(currentDate.getFullYear());
    nextPaymentDate.setMonth(currentDate.getMonth());

    // If nextPaymentDate is still before currentDate, increment the month
    if (nextPaymentDate <= currentDate) {
      let month = nextPaymentDate.getMonth();

      // Check if it's December
      if (month === 11) {
        month = 0; // Set month to January
        nextPaymentDate.setFullYear(currentDate.getFullYear() + 1); // Increment year by 1
      } else {
        month++;   // Increment month by 1
      }

      nextPaymentDate.setMonth(month);
    }

    return nextPaymentDate;
  }

  onExtensionSelectorClick(e: any) {
    e.stopPropagation(); // Prevent the click from immediately triggering the @HostListener
    this.showTooltip(e);
  }

  showTooltip(event: MouseEvent) {
    this.tooltipVisible = true;
    const targetElement = event.target as HTMLElement;

    const rect = (event.target as HTMLElement).getBoundingClientRect();
    this.tooltipPosition.top = rect.bottom + window.scrollY + 'px';
    this.tooltipPosition.left = rect.left + window.scrollX + 'px';

    if (targetElement.id) {
        const elementById = document.getElementById(targetElement.id);
        if (elementById) {
            this.tooltipPosition.width = elementById.getBoundingClientRect().width + 'px';
        }
    }
  }

  onSelectCountry(country: any) {
    this.userForm.controls['country'].setValue(country.name_es);
    this.tooltipVisible = false;
  }

  setSearchTerm(event: any) {
    this.searchTerm = event.target.value;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.tooltipVisible && !this.tooltipRef.nativeElement.contains(event.target) && !this.triggerInputRef.nativeElement.contains(event.target)) {
      this.tooltipVisible = false;
    }
  }

  validateCountryName(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      const valid = this.phoneExtensions.some(ext => ext.name_es === control.value || ext.name_es.toLowerCase() === control.value || ext.name_es.toUpperCase() === control.value);
      return valid ? null : { 'invalidCountryName': { value: control.value } };
    };
  }

  async toggleEmailMarketing() {
    const newValue = !this.userData?.unsubscribedEmailMarketing;
    try {
      await this.apiSdkService.editUserData({ unsubscribedEmailMarketing: newValue } as any);
      this.userData.unsubscribedEmailMarketing = newValue;
      this.showToast(newValue ? 'Marketing emails disabled' : 'Marketing emails enabled', 'success');
    } catch (error) {
      console.error('Error updating email preferences:', error);
      this.showToast('Error updating email preferences', 'error');
    }
  }

  toggleEdit() {
    this.editMode = !this.editMode;
    const allFields = ['name', 'phone', 'country'];
    
    if (this.editMode) {
      // Enable fields
      allFields.forEach(field => {
        this.userForm.get(field)?.enable();
      });
    } else {
      // Disable fields
      allFields.forEach(field => {
        this.userForm.get(field)?.disable();
      });
    }
  }

  cancelEdit() {
    // Reset form to original values
    this.userForm.patchValue(this.userData);
    
    // Exit edit mode
    this.editMode = false;
    
    // Disable all fields
    const allFields = ['name', 'phone', 'country'];
    allFields.forEach(field => {
      this.userForm.get(field)?.disable();
    });
    
    // Clear any validation errors
    Object.values(this.userForm.controls).forEach(control => {
      control.markAsUntouched();
    });
  }

  async save() {
    // First validate the form
    Object.values(this.userForm.controls).forEach(control => {
      control.markAsTouched();
    });

    if (this.userForm.valid) {
      try {
        // Get the form values including disabled fields
        const formData = this.userForm.getRawValue();
        await this.apiSdkService.editUserData(formData);
        
        // Show success toast
        this.showToast('Data saved successfully!', 'success');
        
        // Exit edit mode
        this.toggleEdit();
        
        // Refresh user data
        await this.getUserData();
      } catch (error) {
        console.error('Error saving data:', error);
        this.showToast('Error saving data. Please try again.', 'error');
      }
    } else {
      this.showToast('Please fill in all required fields correctly.', 'warning');
    }
  }

  showToast(message: string, type: 'success' | 'error' | 'warning' = 'success') {
    const toastContainer = document.getElementById('toast-container') || this.createToastContainer();
    
    const toast = document.createElement('div');
    toast.className = `toast-notification toast-${type}`;
    toast.innerHTML = `
      <div class="toast-content">
        <i class="fa-solid ${type === 'success' ? 'fa-check-circle' : type === 'error' ? 'fa-times-circle' : 'fa-exclamation-triangle'}"></i>
        <span>${message}</span>
      </div>
    `;
    
    toastContainer.appendChild(toast);
    
    // Trigger animation
    setTimeout(() => toast.classList.add('show'), 10);
    
    // Remove toast after 3 seconds
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  async manageBilling() {
    try {

      const result = await this.apiSdkService.getCustomerPortalUrl();
      
      if (result?.success?.portalUrl) {
        // Open Stripe customer portal in a new tab
        window.open(result.success.portalUrl, '_blank');
      } else {
        this.showToast('Error getting billing portal URL', 'error');
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      this.showToast('Error opening billing portal', 'error');
    }
  }

  private createToastContainer(): HTMLElement {
    const container = document.createElement('div');
    container.id = 'toast-container';
    container.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
    `;
    document.body.appendChild(container);
    return container;
  }

}

